import { Pagination, Skeleton, Spin } from "antd"
import { Button } from "components"
import WorkPlaceCard from "../components/WorkplaceCard"
import { useState, useMemo, useEffect } from "react"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useGet } from "utils/useFetch"
import { IWorkPlacesEntity, IWorkPlacesRes } from "../typings"
import { debounce } from "lodash"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useNavigate } from "react-router-dom"
import WorkPlaceAnalysisWrapper from "../components/WorkPlaceAnalysisWrapper"
import WorkPlaceFilterWrapper from "./component/WorkPlaceFilterWrapper"
import { WorkplaceName } from "../setup/components/SetupDutyLocations"
import CustomTAndAWrapper from "../components/CustomTAndAWrapper"
import { useAuthStore, useAppNotificationStore } from "stores"
import { toast } from "react-toastify"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { Skeleton as AppSkeleton } from "components/new-stuff/ui/Skeleton"

const Overview = () => {
  const queryClient = useQueryClient()
  const { toast: appToast } = useAppNotificationStore()
  const [canCreateWorkplace] = usePermissions({
    allowedPermissions: [TimeAndAttendancePermission.CanCreateWorkplaces],
  })

  const [page, setPage] = useState({ currentPage: 1, pageSize: 10 })
  const [modalType, setModalType] = useState<"published" | "draft" | "">("")
  const [selectedTimeLine, setSelectedTimeLine] = useState("3d")
  const [selectedFilter, setSelectedFilter] = useState("all")
  const [inputValue, setInputValue] = useState("")
  const [search, setSearch] = useState("")
  const [selectedWorkplace, setSelectedWorkplace] =
    useState<IWorkPlacesEntity>()
  const { user } = useAuthStore((state) => state)
  const companyId = Number(user?.currentCompanyId)
  const navigate = useNavigate()

  // ─── Query key ────────────────────────────────────────────────────────────

  const workplacesQueryKey = `/v1/time-attendance/workplaces?keyword=${search}&page=${page.currentPage}&limit=${page.pageSize}&companyId=${user?.userCompanyId}`

  // ─── Data fetching ────────────────────────────────────────────────────────

  const { isLoading: isLoadingWorkPlacesData, data: workPlacesData } =
    useGet<IWorkPlacesRes>({
      url: workplacesQueryKey,
      instanceKey: "ewaAxiosInstance",
    })

  const { isLoading: isSetupDone, data: setupDone } = useGet<{
    success: boolean
    code: number
    message: string
    data: {
      setup: boolean
    }
  }>({
    url: `/v1/time-attendance/setup/${companyId}`,
    instanceKey: "ewaAxiosInstance",
  })

  // ─── Mutations ────────────────────────────────────────────────────────────

  const { mutate: toggleWorkplace, isLoading: isTogglingWorkplace } =
    useMutation({
      mutationFn: ({
        id,
        status,
      }: {
        id: number | undefined
        status: "published" | "draft"
      }) =>
        ewaAxiosInstance.put(`/v1/time-attendance/workplace-config/${id}`, {
          status,
        }),
      onMutate: async ({ id, status }) => {
        await queryClient.cancelQueries({ queryKey: [workplacesQueryKey] })
        const previous = queryClient.getQueryData<IWorkPlacesRes>([
          workplacesQueryKey,
        ])
        if (previous?.data?.docs) {
          queryClient.setQueryData<IWorkPlacesRes>([workplacesQueryKey], {
            ...previous,
            data: {
              ...previous.data,
              docs: previous.data.docs.map((doc) => {
                if (
                  doc.workplaceConfig &&
                  doc.workplaceConfig.workplaceId === id
                ) {
                  return {
                    ...doc,
                    workplaceConfig: {
                      ...doc.workplaceConfig,
                      status,
                    },
                  }
                }
                return doc
              }),
            },
          })
        }
        return { previous }
      },
      onError: (err, _vars, context) => {
        if (context?.previous) {
          queryClient.setQueryData([workplacesQueryKey], context.previous)
        }
        onQueryError(err)
      },
      onSuccess: () => {
        toast.success("Toggle Workplace Successfully")
        setModalType("")
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [workplacesQueryKey] })
      },
    })

  // ─── Derived data ─────────────────────────────────────────────────────────

  const workplaceDataDocs = workPlacesData?.data?.docs || []

  const draftsCount = useMemo(
    () =>
      workplaceDataDocs.filter(
        (w) => w?.workplaceConfig?.status === "draft" || !w?.workplaceConfig
      ).length,
    [workplaceDataDocs]
  )

  const publishedCount = useMemo(
    () =>
      workplaceDataDocs.filter(
        (w) => w?.workplaceConfig?.status === "published"
      ).length,
    [workplaceDataDocs]
  )

  const inactiveCount = useMemo(
    () =>
      workplaceDataDocs.filter(
        (w) => w?.workplaceConfig && w.workplaceConfig.isActive === false
      ).length,
    [workplaceDataDocs]
  )

  const filteredWorkplaces = useMemo(() => {
    let result = workplaceDataDocs

    if (selectedFilter !== "all") {
      const filter = selectedFilter.toLowerCase()
      if (filter === "draft") {
        result = workplaceDataDocs.filter(
          (w) => !w?.workplaceConfig || w?.workplaceConfig?.status === "draft"
        )
      } else if (filter === "inactive") {
        result = workplaceDataDocs.filter(
          (w) => w?.workplaceConfig && w.workplaceConfig.isActive === false
        )
      } else {
        result = workplaceDataDocs.filter(
          (w) => w?.workplaceConfig?.status === "published"
        )
      }
    }

    return [...result].sort((a, b) => {
      const aPublished = a?.workplaceConfig?.status === "published"
      const bPublished = b?.workplaceConfig?.status === "published"
      if (aPublished && !bPublished) return -1
      if (!aPublished && bPublished) return 1
      return 0
    })
  }, [selectedFilter, workplaceDataDocs])

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleToggleSelectedWorkplace = (status: "published" | "draft") => {
    const configId = selectedWorkplace?.workplaceConfig?.workplaceId
    if (!configId) return
    toggleWorkplace({ id: configId, status })
  }

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value)
        setPage((prev) => ({ ...prev, currentPage: 1 }))
      }, 500),
    []
  )

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <CustomTAndAWrapper>
      {isSetupDone ? (
        // A full-viewport spinner for what resolves into cards and a table.
        // Placeholders in that shape keep the page the right size.
        <div className="flex flex-col gap-4 py-4" aria-busy="true">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <AppSkeleton key={i} variant="block" width="100%" height="106px" />
            ))}
          </div>
          <AppSkeleton variant="block" width="100%" height="320px" />
        </div>
      ) : (
        <>
          {!setupDone?.data.setup ? (
            <div className="bg-white h-screen py-12 px-8 flex flex-col items-center justify-center">
              <img
                src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/0e225ab3-c58b-49a7-a9f6-6b028b754d0a.svg"
                alt="setup"
                width={600}
                height={376}
                className="w-[600px] h-[376px] object-contain mb-12"
              />
              <div className="text-center">
                <h5 className="text-3xl text-[#0F172BCC] font-bold text-center mb-12">
                  Time and Attendance Module
                </h5>
                <h4 className="text-[#62748E] text-sm text-center max-w-[524px] leading-[22px] mb-6">
                  Manage your team's schedules, track attendance, and simplify
                  shift management — with optional payroll integration. Setup
                  takes about 5-10 minutes.
                </h4>
                <Button
                  type="button"
                  color="primary"
                  className="font-semibold px-6 text-white h-10"
                  onClick={() =>
                    navigate("/dashboard/time-and-attendance/setup")
                  }
                >
                  Set Up Time &amp; Attendance
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white py-4 px-3">
              <h5 className="text-slate-900 text-2xl mb-1 font-bold">
                Time and attendance
              </h5>
              <p className="text-slate-500 text-sm">
                Manage your team's schedules, track attendance and simplify
                shift management.
              </p>

              <WorkPlaceAnalysisWrapper
                title="Stats across all workplaces"
                timeline={selectedTimeLine}
                setSelectedTimeline={setSelectedTimeLine}
              />

              <WorkPlaceFilterWrapper
                debouncedSetSearch={debouncedSetSearch}
                draftsCount={draftsCount}
                inputValue={inputValue}
                publishedCount={publishedCount}
                inactiveCount={inactiveCount}
                selectedFilter={selectedFilter}
                setInputValue={setInputValue}
                setSelectedFilter={setSelectedFilter}
                totalDocs={workPlacesData?.data?.totalDocs || 0}
              />

              <div>
                {isLoadingWorkPlacesData ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {Array.from({ length: 4 }).map(() => (
                      <div className="border-2 border-[#E2E8F0] rounded-md p-2 flex-1 mt-2 flex justify-center items-center flex-col">
                        <Skeleton
                          active
                          title={false}
                          paragraph={{ rows: 4 }}
                        />
                        <Skeleton.Input
                          active
                          style={{ marginTop: "5px" }}
                        />
                        <Skeleton.Input
                          active
                          style={{ marginTop: "5px" }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <Spin spinning={isTogglingWorkplace}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2 pt-2">
                      {filteredWorkplaces.map((workplace) => (
                        <WorkPlaceCard
                          key={workplace.id}
                          status={
                            workplace.workplaceConfig?.status === "published"
                              ? "Published"
                              : "Draft"
                          }
                          workplaceId={workplace.workplaceConfig?.workplaceId}
                          office={workplace.displayName}
                          address={workplace.fullAddress}
                          recordsStatusCount={workplace?.recordStatus || 0}
                          employeeCount={workplace?.employees?.length ?? 0}
                          dutyLocationsCount={
                            workplace.officeDutyLocations?.length || 0
                          }
                          shiftPlansCount={workplace.shiftPlans?.length || 0}
                          onToggle={() => {
                            setModalType(
                              (workplace?.workplaceConfig?.status ?? "") as
                                | "published"
                                | "draft"
                                | ""
                            )
                            setSelectedWorkplace(workplace)
                          }}
                          workplaceDetail={workplace}
                        />
                      ))}
                      <button
                        color="neutral"
                        className="flex items-center py-2 justify-center flex-col h-80 w-full bg-white border border-slate-200"
                        onClick={() => {
                          if (!canCreateWorkplace) {
                            appToast.unauthorized()
                            return
                          }
                          navigate(
                            "/dashboard/time-and-attendance/setup?createWorkplace=1"
                          )
                        }}
                      >
                        <img
                          src={
                            "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/42014f85-a6cf-4d94-9053-f9dcea7262b4.png"
                          }
                          height={120}
                          width={120}
                          alt="create workplace"
                        />
                        <span className="flex items-center gap-2 font-semibold">
                          <img
                            src={
                              "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/076a9c7e-2bef-4abc-a252-14f22ba21cbb.png"
                            }
                            width={15}
                            height={15}
                            alt="arrow"
                          />
                          Create workplace
                        </span>
                      </button>
                    </div>
                  </Spin>
                )}
              </div>

              <div className="flex justify-start mt-6 mb-3">
                <Pagination
                  current={page.currentPage}
                  pageSize={page.pageSize}
                  total={workPlacesData?.data?.totalDocs || 0}
                  showSizeChanger
                  pageSizeOptions={[5, 10, 20, 50]}
                  onChange={(page, pageSize) =>
                    setPage({ currentPage: page, pageSize })
                  }
                />
              </div>
            </div>
          )}
        </>
      )}

      <CustomModal
        rootClassName="ta-scope"
        open={modalType === "published" || modalType === "draft"}
        width={500}
        title="Workplace Time & Attendance Status"
        onCancel={() => setModalType("")}
        footer={
          <div className="w-full justify-end flex gap-4">
            <Button
              onClick={() => setModalType("")}
              className="font-semibold border border-slate-300 rounded-md"
              disabled={isTogglingWorkplace}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="font-semibold"
              onClick={() =>
                handleToggleSelectedWorkplace(
                  modalType === "draft" ? "published" : "draft"
                )
              }
              disabled={!selectedWorkplace || isTogglingWorkplace}
            >
              {modalType === "draft" ? "Yes, turn on" : "Yes, turn off"}
            </Button>
          </div>
        }
      >
        <div>
          <p className="font-semibold mb-1 flex items-center gap-1">
            Turn {modalType === "draft" ? "on" : "off"}
            <WorkplaceName name={selectedWorkplace?.displayName ?? ""} />?
          </p>
          <p className="text-sm">
            Turning {modalType === "draft" ? "on" : "off"} Time & Attendance for
            this workplace will {modalType === "draft" ? "start" : "stop"}{" "}
            attendance tracking for assigned employees. Are you sure?
          </p>
        </div>
      </CustomModal>
    </CustomTAndAWrapper>
  )
}

export default Overview
